import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Landmark, HandCoins, Ban, Undo2, X } from "lucide-react";

const brl = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fdate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).length === 10 ? iso + "T12:00:00" : iso);
    return d.toLocaleDateString("pt-BR");
  } catch { return String(iso); }
};

const STATUSES = ["Em aberto", "Vencido", "Parcialmente pago", "Pago", "Cancelado"];
const STATUS_CLS = {
  "Em aberto": "text-sky-400 border-sky-900/60",
  "Vencido": "text-red-400 border-red-900/60",
  "Parcialmente pago": "text-amber-400 border-amber-900/60",
  "Pago": "text-emerald-400 border-emerald-900/60",
  "Cancelado": "text-slate-500 border-slate-700",
};
const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

export default function AdminFinanceiro() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [pay, setPay] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", note: "", reference: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/receivables?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch((err) => { setData({ items: [], summary: {} }); toast.error(formatApiError(err)); });
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPay = (r) => {
    setPay(r);
    setPayForm({ amount: String(r.amount_open ?? ""), note: "", reference: "" });
  };

  const submitPay = async () => {
    setSaving(true);
    try {
      await api.post(`/receivables/${pay.id}/payments`, {
        amount: Number(payForm.amount) || 0, note: payForm.note, reference: payForm.reference,
      });
      toast.success("Pagamento registrado.");
      setPay(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const cancelRec = async (r) => {
    const reason = window.prompt(`Cancelar o recebível ${r.receipt_number} parcela ${r.installment}/${r.installments_total}?\nMotivo (opcional):`, "");
    if (reason === null) return;
    try {
      await api.post(`/receivables/${r.id}/cancel`, { reason });
      toast.success("Recebível cancelado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reverseRec = async (r) => {
    const reason = window.prompt(`Estornar os pagamentos deste recebível (${brl(r.amount_original - r.amount_open)} recebidos)?\nMotivo (obrigatório p/ auditoria):`, "");
    if (reason === null) return;
    try {
      await api.post(`/receivables/${r.id}/reverse-payments`, { reason });
      toast.success("Pagamentos estornados — recebível reaberto.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }
  const s = data.summary || {};

  return (
    <div data-testid="financeiro-page">
      <h1 className="display text-3xl font-extrabold uppercase">Financeiro — Contas a Receber</h1>
      <p className="mt-1 text-sm text-slate-500">
        Lançamentos gerados automaticamente pelas retiradas de peças. O registro representa a obrigação financeira — não a confirmação de pagamento.
      </p>
      <Link to="/financeiro/pagar" data-testid="fin-payables-link"
        className="mt-1 inline-block text-xs text-orange-400 hover:text-orange-300 uppercase tracking-wider transition-colors">
        Contas a Pagar →
      </Link>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Em aberto", value: s.open_total, testId: "fin-summary-open", cls: "text-sky-400" },
          { label: "Vencido", value: s.overdue_total, testId: "fin-summary-overdue", cls: "text-red-400" },
          { label: "Parcialmente pago", value: s.partial_total, testId: "fin-summary-partial", cls: "text-amber-400" },
          { label: "Recebido", value: s.paid_total, testId: "fin-summary-paid", cls: "text-emerald-400" },
        ].map((c) => (
          <div key={c.label} className="card-tactical rounded-md p-4" data-testid={c.testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className={`mt-1 mono text-lg font-bold ${c.cls}`}>{brl(c.value)}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <select data-testid="fin-filter-status" value={status}
          onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !mt-0 w-52`}>
          <option value="">Todos os status</option>
          {STATUSES.map((st) => <option key={st}>{st}</option>)}
        </select>
        <input data-testid="fin-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Buscar cliente, ORC, OP, RR..." className={`${inputCls} !mt-0 flex-1 min-w-52`} />
        <button data-testid="fin-search-button" onClick={load}
          className="pill-accent rounded-md px-5 py-2 text-xs font-semibold uppercase tracking-wider transition">
          Buscar
        </button>
      </div>

      <div className="mt-4 card-tactical rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="fin-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-4 py-3">Vencimento</th>
              <th className="px-4 py-3">Cliente / Documentos</th>
              <th className="px-4 py-3">Parcela</th>
              <th className="px-4 py-3">Forma</th>
              <th className="px-4 py-3 text-right">Valor</th>
              <th className="px-4 py-3 text-right">Em aberto</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((r) => (
              <tr key={r.id} className="border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors"
                data-testid={`fin-row-${r.id}`}>
                <td className="px-4 py-3 mono text-xs text-slate-300">
                  {fdate(r.due_date)}
                  {r.paid_at && <div className="text-[10px] text-emerald-500">pago em {fdate(r.paid_at)}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs text-slate-200">{r.client_name}</div>
                  <div className="mono text-[10px] text-slate-500">
                    {r.quote_number} · {r.op_number} · {r.receipt_number} (R{r.receipt_seq})
                  </div>
                  <div className="text-[10px] text-slate-600">{r.description}</div>
                </td>
                <td className="px-4 py-3 mono text-xs text-slate-300">{r.installment}/{r.installments_total}</td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  {r.payment_method_label}
                  {r.needs_review && <div className="text-[10px] text-amber-400">revisar condição</div>}
                </td>
                <td className="px-4 py-3 mono text-xs text-right text-slate-200">{brl(r.amount_original)}</td>
                <td className="px-4 py-3 mono text-xs text-right text-orange-400">{brl(r.amount_open)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_CLS[r.status] || ""}`}
                    data-testid={`fin-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-3">
                    {["Em aberto", "Vencido", "Parcialmente pago"].includes(r.status) && (
                      <button data-testid={`fin-pay-${r.id}`} onClick={() => openPay(r)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-emerald-400 transition-colors">
                        <HandCoins className="w-3 h-3" /> Pagamento
                      </button>
                    )}
                    {r.has_payments && (
                      <button data-testid={`fin-reverse-${r.id}`} onClick={() => reverseRec(r)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-amber-400 transition-colors">
                        <Undo2 className="w-3 h-3" /> Estornar
                      </button>
                    )}
                    {!r.has_payments && r.status !== "Cancelado" && (
                      <button data-testid={`fin-cancel-${r.id}`} onClick={() => cancelRec(r)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-400 transition-colors">
                        <Ban className="w-3 h-3" /> Cancelar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!(data.items || []).length && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-xs text-slate-600">
                <Landmark className="w-5 h-5 mx-auto mb-1.5 text-slate-800" strokeWidth={1.25} />
                Nenhum recebível encontrado — os lançamentos nascem das retiradas de peças.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setPay(null)}>
          <div className="card-tactical rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}
            data-testid="pay-modal">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-extrabold uppercase">Registrar pagamento</h2>
              <button onClick={() => setPay(null)} data-testid="pay-close" className="text-slate-500 hover:text-slate-300" aria-label="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500 mono">
              {pay.client_name} · {pay.receipt_number} · parcela {pay.installment}/{pay.installments_total} · em aberto {brl(pay.amount_open)}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Valor recebido (R$)</label>
                <input data-testid="pay-amount-input" type="number" step="0.01" min="0.01"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Observação</label>
                <input data-testid="pay-note-input" value={payForm.note}
                  onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Referência (comprovante — evita registro duplicado)</label>
                <input data-testid="pay-ref-input" value={payForm.reference}
                  onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className={inputCls} />
              </div>
            </div>
            <button data-testid="pay-submit" onClick={submitPay} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
              Confirmar pagamento
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
